import { useState, useCallback, useEffect } from 'react';
import { optionToId } from '../utils/option-to-id.js';

/**
 * Hook that integrates all required functionality by listboxes.
 *
 * In particular accesibility and keyboard shortcuts are managed by this hook
 * by keeping track of the activeDescendant and triggering toggleValue when
 * necessary.
 */
function useListbox({
  id,
  options,
  toggleValue
}, listboxRef) {
  const [activeDescendantIndex, setActiveDescendantIndex] = useState(0);
  const activeDescendantId = optionToId(id, options[activeDescendantIndex]);

  /**
   * Callback which should be triggered when a given option in the listbox is
   * clicked.
   */
  const onOptionClick = useCallback(index => {
    const clickedOption = options[index];
    if (clickedOption === undefined) {
      throw new Error("useListbox.onOptionClick: unable to identify clicked option");
    }
    setActiveDescendantIndex(index);
    toggleValue(getOptionValue(clickedOption), clickedOption);
  }, [options, toggleValue]);

  /**
   * Binds to all keystrokes to properly handle keyboard events when the
   * listbox is active
   */
  useEffect(() => {
    const listbox = listboxRef.current;
    if (!listbox) {
      return;
    }
    const handleKeydown = evt => {
      switch (evt.key) {
        case " ":
        case "Enter":
          {
            evt.preventDefault();
            const activeOption = options[activeDescendantIndex];
            if (activeOption === undefined) {
              throw new Error("useListbox.handleKeyDown: unable to identify toggled option");
            }
            toggleValue(getOptionValue(activeOption), activeOption);
            break;
          }
        case "ArrowDown":
          {
            evt.preventDefault();
            const nextIndex = activeDescendantIndex === options.length - 1 ? 0 : activeDescendantIndex + 1;
            setActiveDescendantIndex(nextIndex);
            break;
          }
        case "ArrowUp":
          {
            evt.preventDefault();
            const nextIndex = activeDescendantIndex === 0 ? options.length - 1 : activeDescendantIndex - 1;
            setActiveDescendantIndex(nextIndex);
            break;
          }
        case "Home":
          {
            evt.preventDefault();
            setActiveDescendantIndex(0);
            break;
          }
        case "End":
          {
            evt.preventDefault();
            setActiveDescendantIndex(options.length - 1);
            break;
          }
      }
    };
    const onAnyElementFocus = evt => {
      if (activeDescendantIndex === 0) {
        return;
      }

      // Ensure that, if focus ever leaves our listbox, that the active
      // descendant is reset to 0
      if (!(evt.target instanceof Node) || !listbox.contains(evt.target)) {
        setActiveDescendantIndex(0);
      }
    };
    window.addEventListener("focus", onAnyElementFocus, {
      capture: true
    });
    listbox.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("focus", onAnyElementFocus, {
        capture: true
      });
      listbox.removeEventListener("keydown", handleKeydown);
    };
  }, [activeDescendantIndex, options.length, toggleValue, options, listboxRef]);

  // On descendant change, ensure that the newly focused descendant is visible
  // by scrolling if necessary
  useEffect(() => {
    if (!listboxRef.current?.contains(document.activeElement)) {
      return;
    }
    listboxRef.current?.querySelector(`#${activeDescendantId}`)?.focus();
  }, [activeDescendantId, listboxRef]);
  return {
    activeDescendantId,
    onOptionClick
  };
}
function getOptionValue(option) {
  if (typeof option === "object") {
    return option.id;
  }
  return option;
}

export { useListbox };
//# sourceMappingURL=listbox.js.map
